import React, { useEffect } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Radio,
  Row,
  Select,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { IDataModal } from '@/interfaces/dataModal';
import { IUser } from '@/interfaces/user';
import { addUser, editUser } from '@/adapters/user';
import { getUserRoleOptions } from '@/helpers/roleHelper';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import { useAuth } from '@/contexts/AuthContext';

interface IProps {
  dataModal?: IDataModal<Partial<IUser>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const UserAddModal = ({ dataModal, handleOk, handleCancel }: IProps) => {
  const { userInfo } = useAuth();
  const [form] = Form.useForm<Partial<IUser>>();
  const mutations = useMutation({
    mutationFn: async (data: Partial<IUser>) =>
      dataModal?.id
        ? await editUser(dataModal?.id.toString(), data)
        : await addUser(data),
    mutationKey: ['actionUser'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        const message = dataModal?.id
          ? 'อัพเดทผู้ใช้งานสำเร็จ'
          : 'สร้างผู้ใช้งานสำเร็จ';
        notification.success({
          message: message,
        });
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IUser>) => {
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (dataModal?.item) {
      form.setFieldsValue({
        ...dataModal.item,
        // Scope to current user's BU when set; otherwise keep the edited user's BU
        businessUnitId:
          userInfo?.bu || dataModal.item.businessUnitId || undefined,
      });
    }
  }, [dataModal?.item, form, userInfo?.bu]);

  return (
    <Modal
      title={
        dataModal?.item
          ? `ผู้ใช้งาน : ${dataModal?.item?.firstName}`
          : 'ผู้ใช้งานใหม่'
      }
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={600}
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="productGroup"
          loading={mutations.isPending}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="productGroup"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
          initialValues={
            {
              businessUnitId: userInfo?.bu,
            } as IUser
          }
        >
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label="สิทธิ์การใช้งาน"
                name="role"
                rules={[{ required: true, message: 'ระบุ สิทธิ์การใช้งาน' }]}
              >
                <Select
                  className="md:min-w-[150px]"
                  size="large"
                  placeholder="เลือกประเภท"
                  options={getUserRoleOptions()}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="BU" name="businessUnitId">
                <BusinessUnitSelector
                  handleSelectData={id =>
                    form.setFieldValue('businessUnitId', id)
                  }
                  disabled={!!userInfo?.bu}
                />
              </Form.Item>
            </Col>
            <Col span={dataModal?.item ? 24 : 12}>
              <Form.Item
                label="USERNAME"
                name="userName"
                rules={[{ required: true, message: 'ระบุ USERNAME' }]}
              >
                <Input
                  size="large"
                  autoComplete="nope"
                  readOnly={!!dataModal?.item}
                />
              </Form.Item>
            </Col>
            {!dataModal?.item && (
              <Col span={12}>
                <Form.Item
                  label="รหัสผ่าน"
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: 'ระบุ รหัสผ่าน (อย่างน้อย 6 ตัวอักษร)',
                      min: 6,
                    },
                  ]}
                >
                  <Input.Password
                    size="large"
                    autoComplete="nope"
                    minLength={6}
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={12}>
              <Form.Item
                label="ชื่อ"
                name="firstName"
                rules={[{ required: true, message: 'ระบุชื่อ' }]}
              >
                <Input size="large" autoComplete="nope" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="นามสกุล"
                name="lastName"
                rules={[{ required: true, message: 'ระบุนามสกุล' }]}
              >
                <Input size="large" autoComplete="nope" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="ชื่อเล่น" name="nickName">
                <Input size="large" autoComplete="nope" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="เบอร์โทร"
                name="phoneNumber"
                rules={[{ required: true, message: 'ระบุเบอร์โทร' }]}
              >
                <Input size="large" autoComplete="nope" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="อีเมล์"
                name="email"
                rules={[{ required: true, message: 'ระบุอีเมล์' }]}
              >
                <Input size="large" autoComplete="nope" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="ที่อยู่" name="address">
                <Input.TextArea rows={2} size="large" autoComplete="nope" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="สถานะ"
                name="isActive"
                rules={[{ required: true, message: 'เลือกสถานะ' }]}
                initialValue={true}
              >
                <Radio.Group>
                  <Radio value={true}>ใช้งาน</Radio>
                  <Radio value={false}>ไม่ใช้งาน</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="2FA"
                name="superAdmin"
                rules={[{ required: true, message: 'เลือก 2FA' }]}
                initialValue={true}
              >
                <Radio.Group>
                  <Radio value={false}>ใช้งาน</Radio>
                  <Radio value={true}>ไม่ใช้งาน</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default UserAddModal;
